// Pair-endpoint snippet for browser / JavaScript. Mirrors the other-engine
// references: persists the token + install id in localStorage, exposes a
// module singleton, and uses fetch + SubtleCrypto for the signed POST.

export function buildPairWebSnippet(apiKey: string, functionsBaseUrl: string): string {
  return `// player-base-pairing.js (ES module, served over HTTPS or localhost)
// One-off pairing handshake. Import the singleton anywhere in your web
// build; the first import seeds the install id and reuses the saved
// player token on subsequent loads.
//
// Flow:
//   1. Player taps the recruitment button in Discord -> bot DMs them a
//      short code.
//   2. Your in-game pairing UI calls PlayerBasePairing.redeem(code).
//   3. On success, the returned promise resolves with the playerToken.
//      The token plus the install id are written to localStorage and
//      reused on every subsequent /events call (see PlayerBaseEvents).
//
// The install id is generated once per device and kept across re-pairs
// (unpair() leaves it intact) so the backend can correlate sessions
// from the same install. SubtleCrypto requires a secure context
// (HTTPS or localhost); itch.io and most static hosts already are.

const API_KEY = "${apiKey}";
const PAIR_URL = "${functionsBaseUrl}/pair";
const STORE_PLAYER_TOKEN = "playerbase.player_token";
const STORE_INSTALL_ID = "playerbase.install_id";

let playerToken = localStorage.getItem(STORE_PLAYER_TOKEN) ?? "";
let installId = localStorage.getItem(STORE_INSTALL_ID) ?? "";
if (!installId) {
  installId = crypto.randomUUID();
  localStorage.setItem(STORE_INSTALL_ID, installId);
}

export const PlayerBasePairing = {
  isPaired: () => playerToken !== "",
  get playerToken() { return playerToken; },
  get installId() { return installId; },

  // Call from your in-game pairing UI once the player has pasted the
  // code from the Discord DM. Resolves with the playerToken or rejects
  // with an Error whose message is the server's error token.
  async redeem(pairingCode) {
    const body = JSON.stringify({
      code: String(pairingCode).trim(),
      installId,
    });
    const signature = await hmacSha256Hex(API_KEY, body);
    const res = await fetch(PAIR_URL, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-playerbase-signature": signature,
      },
      body,
    });
    if (!res.ok) {
      let reason = "http_" + res.status;
      try {
        const err = await res.json();
        if (err && err.error) reason = err.error;
      } catch { /* leave reason as http_<code> */ }
      throw new Error(reason);
    }
    const json = await res.json();
    if (!json || !json.playerToken) throw new Error("bad_response");
    playerToken = json.playerToken;
    localStorage.setItem(STORE_PLAYER_TOKEN, playerToken);
    return playerToken;
  },

  // Wipe the saved token. Use for a "Sign out / unpair" affordance.
  // Install id is kept so a later re-pair correlates with the same
  // install.
  unpair() {
    playerToken = "";
    localStorage.removeItem(STORE_PLAYER_TOKEN);
  },
};

async function hmacSha256Hex(key, body) {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, enc.encode(body));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
`;
}
